import React, { useState, useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  Paper,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
} from '@material-ui/core';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import './ApiUsage.css';
const number = (n) => Number(n || 0).toLocaleString('ko-KR');
const client = (r) =>
  r.client_seq == null
    ? '미인증 요청'
    : r.client_code || `Client #${r.client_seq}`;
function Table({ rows, columns }) {
  return (
    <div className="api-usage-scroll">
      <table>
        <thead>
          <tr>
            {columns.map(([label]) => (
              <th key={label}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((r, i) => (
              <tr key={i}>
                {columns.map(([label, render]) => (
                  <td key={label}>{render(r)}</td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length}>조회 결과가 없습니다.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
export default function ApiUsage() {
  const [draft, setDraft] = useState({
    start: dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
    end: dayjs().format('YYYY-MM-DD'),
    client: '',
  });
  const [errorDraft, setErrorDraft] = useState({ status: '', path: '' });
  const [errorFilters, setErrorFilters] = useState({ status: '', path: '' });
  const [updatedAt, setUpdatedAt] = useState(null);
  const [filters, setFilters] = useState(draft),
    [page, setPage] = useState(0),
    [reload, setReload] = useState(0);
  const [data, setData] = useState(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    axios
      .get('/api/apiUsage', { params: { ...filters, ...errorFilters, page } })
      .then((r) => {
        if (active) {
          setData(r.data);
          setUpdatedAt(new Date());
        }
      })
      .catch((e) => {
        if (active) {
          setError(e.response?.data?.msg || '통계를 불러오지 못했습니다.');
          setData(null);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [filters, errorFilters, page, reload]);
  const change = (key) => (e) => setDraft({ ...draft, [key]: e.target.value });
  const search = () => {
    setPage(0);
    setFilters({ ...draft });
  };
  const changeError = (key) => (e) =>
    setErrorDraft({ ...errorDraft, [key]: e.target.value });
  const searchErrors = () => {
    setPage(0);
    setErrorFilters({ ...errorDraft });
  };
  const quickRange = (kind) => {
    const end = dayjs();
    const start =
      kind === 'month'
        ? end.startOf('month')
        : end.subtract(Number(kind) - 1, 'day');
    const next = {
      ...draft,
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
    };
    setDraft(next);
    setFilters(next);
    setPage(0);
  };
  const daily = [];
  if (data) {
    const map = new Map(data.daily.map((r) => [r.day, r]));
    for (
      let d = dayjs(filters.start);
      !d.isAfter(dayjs(filters.end), 'day');
      d = d.add(1, 'day')
    ) {
      const day = d.format('YYYY-MM-DD');
      daily.push({
        day,
        total: 0,
        unauthorized: 0,
        forbidden: 0,
        serverErrors: 0,
        ...map.get(day),
      });
    }
  }
  const s = data?.summary || {};
  return (
    <div className="api-usage">
      <Typography variant="h4">API 사용 현황</Typography>
      <p>
        고객별 호출량과 인증 오류를 확인합니다. 날짜는 API DB의 시간 기준입니다.
      </p>
      <div className="api-usage-updated" role="status">
        마지막 갱신:{' '}
        {updatedAt
          ? dayjs(updatedAt).format('YYYY-MM-DD HH:mm:ss')
          : '아직 조회되지 않았습니다.'}
      </div>
      <Paper
        className="api-usage-panel"
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          search();
        }}
      >
        <div className="api-usage-filters">
          <TextField
            required
            type="date"
            label="시작일"
            value={draft.start}
            onChange={change('start')}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            required
            type="date"
            label="종료일"
            value={draft.end}
            onChange={change('end')}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="고객"
            value={draft.client}
            onChange={change('client')}
          >
            <MenuItem value="">전체 고객</MenuItem>
            <MenuItem value="anonymous">미인증 요청</MenuItem>
            {data?.clients.map((c) => (
              <MenuItem key={c.client_seq} value={String(c.client_seq)}>
                {c.client_code}
              </MenuItem>
            ))}
          </TextField>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            startIcon={<SearchIcon />}
            disabled={loading}
          >
            조건 적용
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            disabled={loading}
            onClick={() => setReload(reload + 1)}
          >
            최신 데이터 갱신
          </Button>
        </div>
        <div className="api-usage-quick-ranges">
          <span>빠른 조회</span>
          {[
            ['1', '오늘'],
            ['7', '최근 7일'],
            ['30', '최근 30일'],
            ['month', '이번 달'],
          ].map(([kind, label]) => (
            <Button
              key={kind}
              type="button"
              size="small"
              variant="outlined"
              disabled={loading}
              onClick={() => quickRange(kind)}
            >
              {label}
            </Button>
          ))}
        </div>
        <small>
          빠른 조회는 선택한 고객과 기간을 즉시 적용합니다. 최대 93일 조회
          가능합니다.
        </small>
        <p className="api-usage-applied">
          적용 중: {filters.start} ~ {filters.end} ·{' '}
          {filters.client === 'anonymous'
            ? '미인증 요청'
            : filters.client
            ? data?.clients.find((c) => String(c.client_seq) === filters.client)
                ?.client_code || `Client #${filters.client}`
            : '전체 고객'}
        </p>
      </Paper>
      {loading && (
        <div role="status" className="api-usage-loading">
          <CircularProgress size={22} /> 불러오는 중입니다.
        </div>
      )}
      {error && (
        <Paper role="alert" className="api-usage-error">
          {error}
        </Paper>
      )}
      {data && (
        <div aria-busy={loading} style={{ opacity: loading ? 0.5 : 1 }}>
          <Grid container spacing={2}>
            {[
              ['오늘 호출', number(data.current.today)],
              ['이번 달 호출', number(data.current.month)],
              ['선택 기간 호출', number(s.total)],
              [
                'HTTP 성공률 (2xx)',
                s.total ? `${((s.success / s.total) * 100).toFixed(1)}%` : '—',
              ],
              ['인증 실패 (401)', number(s.unauthorized)],
              ['권한 거부 (403)', number(s.forbidden)],
              ['서버 오류 (5xx)', number(s.serverErrors)],
            ].map(([label, value]) => (
              <Grid item xs={12} sm={6} md={3} key={label}>
                <Paper className="api-usage-card">
                  <span>{label}</span>
                  <strong>{value}</strong>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <p className="api-usage-success-note">
            HTTP 성공률은 전체 요청 중 HTTP 2xx 응답의 비율입니다. 응답 본문의
            업무 처리 성공 여부와는 다를 수 있습니다.
          </p>
          <Paper className="api-usage-panel">
            <Typography variant="h6">일별 호출 추이</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tickFormatter={(v) => v.slice(5)} />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(value) => number(value)} />
                <Legend />
                <Line
                  name="전체"
                  dataKey="total"
                  stroke="#536dfe"
                  dot={false}
                />
                <Line
                  name="인증 실패"
                  dataKey="unauthorized"
                  stroke="#d64155"
                  dot={false}
                />
                <Line
                  name="권한 거부"
                  dataKey="forbidden"
                  stroke="#d69b28"
                  dot={false}
                />
                <Line
                  name="서버 오류 (5xx)"
                  dataKey="serverErrors"
                  stroke="#8e44ad"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Paper className="api-usage-panel">
                <Typography variant="h6">고객별 사용량 · 상위 50</Typography>
                <Table
                  rows={data.byClient}
                  columns={[
                    ['고객', client],
                    ['호출', (r) => number(r.total)],
                    ['오류 (4xx·5xx)', (r) => number(r.errors)],
                  ]}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Paper className="api-usage-panel">
                <Typography variant="h6">API별 사용량 · 상위 20</Typography>
                <Table
                  rows={data.byEndpoint}
                  columns={[
                    ['API', (r) => `${r.method} ${r.request_path}`],
                    ['호출', (r) => number(r.total)],
                    ['오류', (r) => number(r.errors)],
                  ]}
                />
              </Paper>
            </Grid>
          </Grid>
          <Paper className="api-usage-panel">
            <Typography variant="h6">최근 오류</Typography>
            <p>
              미인증 요청은 고객 식별에 실패한 기록입니다. 401의 상세 사유는
              현재 로그에 저장되지 않습니다.
            </p>
            <p>
              오류 검색은 위에 적용된 기간·고객을 유지하며, 아래 오류 목록의
              응답 코드와 경로 조건만 적용합니다.
            </p>
            <div className="api-usage-filters">
              <TextField
                select
                label="응답 코드"
                value={errorDraft.status}
                onChange={changeError('status')}
              >
                <MenuItem value="">전체 오류</MenuItem>
                {[400, 401, 403, 404, 422, 429, 500, 502, 503, 504].map((c) => (
                  <MenuItem key={c} value={String(c)}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="경로 포함 검색"
                value={errorDraft.path}
                onChange={changeError('path')}
                inputProps={{ maxLength: 200 }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<SearchIcon />}
                disabled={loading}
                onClick={searchErrors}
              >
                오류 검색
              </Button>
            </div>
            <Table
              rows={data.errors}
              columns={[
                [
                  '시간',
                  (r) => dayjs(r.created_at).format('YYYY-MM-DD HH:mm:ss'),
                ],
                ['고객', client],
                ['키 번호', (r) => r.key_seq ?? '—'],
                ['API', (r) => `${r.method} ${r.request_path}`],
                ['필요 권한', (r) => r.required_scope],
                [
                  '응답',
                  (r) => (
                    <b className="api-usage-status">{r.response_status}</b>
                  ),
                ],
              ]}
            />
            <div className="api-usage-pagination">
              <Button
                disabled={loading || page === 0}
                onClick={() => setPage(page - 1)}
              >
                이전
              </Button>
              <span>{page + 1} 페이지 · 최대 50건</span>
              <Button
                disabled={loading || !data.hasMore}
                onClick={() => setPage(page + 1)}
              >
                다음
              </Button>
            </div>
          </Paper>
        </div>
      )}
    </div>
  );
}
